import { PagingControls } from '../../../components/ui/PagingControls/PagingControls';
import { LoadingContainer } from '../../../components/ui/LoadingContainer/LoadingContainer';
import { SortableHeader } from '../../../components/ui/SortableHeader/SortableHeader';
import Container from '../../ui/Container/Container';
import styles from './SystemCodeLookup.module.css';
import useSystemCodeLookup from './useSystemCodeLookup';
import { ISystemCodeDto } from '../../../utils/constants/interfaces';

interface SystemCodeLookupProps {
  systemCodeId?: string;
  visible?: boolean;
  modalView?: boolean;
  requireKeywords?: boolean;
  onChange: (systemCodeId: string) => void;
}

export const SystemCodeLookup = ({
  visible = true,
  modalView = false,
  requireKeywords = false,
  onChange
}: SystemCodeLookupProps) => {
  const manager = useSystemCodeLookup(requireKeywords, onChange);

  if (!visible) return <></>;

  return (
    <div className={styles.mainContainer}>
      {/* Filters Section - Now Above the Table */}
      <Container className={styles.filtersSection}>
        <div className="row g-3">
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="codeValue">System Code Search</label>
              <input
                id="codeValue"
                type="text"
                className="form-control"
                placeholder="Search by code value"
                onChange={(e) => {
                  e.preventDefault();
                  manager.debouncedSystemCodeSearch(e.target.value);
                }}
              />
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label>Code Type</label>
              <LoadingContainer isLoading={manager.loadingCodeTypes}>
                <select
                  id="codeType"
                  className="form-control"
                  value={manager.searchParameters.filters.codeType}
                  onChange={(e) => {
                    e.preventDefault();
                    manager.handleCodeTypeChange(e.target.value);
                  }}>
                  <option value="">All Types</option>
                  {manager.codeTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </LoadingContainer>
            </div>
          </div>
        </div>
      </Container>

      {/* Results Section - Now Below the Filters */}
      <Container>
        <LoadingContainer isLoading={manager.loadingSearch}>
          <Container
            visible={
              manager.hasSearchParameters && manager.systemCodePagedResults.resultCount === 0
            }>
            <div>No system codes found.</div>
          </Container>
          <Container visible={manager.systemCodePagedResults.resultCount > 0}>
            <>
              <div
                className={
                  modalView ? styles.lookupSearchResultsModal : styles.lookupSearchResults
                }>
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <SortableHeader
                        text="Code Type"
                        sortField="codeType"
                        active={manager.searchParameters.sortField === 'codeType'}
                        sortDirection={manager.searchParameters.sortDirection}
                        onClick={manager.changeSort}
                      />
                      <SortableHeader
                        text="Code Value"
                        sortField="codeValue"
                        active={manager.searchParameters.sortField === 'codeValue'}
                        sortDirection={manager.searchParameters.sortDirection}
                        onClick={manager.changeSort}
                      />
                      <SortableHeader
                        text="Description"
                        sortField="description"
                        active={manager.searchParameters.sortField === 'description'}
                        sortDirection={manager.searchParameters.sortDirection}
                        onClick={manager.changeSort}
                      />
                      <SortableHeader
                        text="Active"
                        sortField="active"
                        active={manager.searchParameters.sortField === 'active'}
                        sortDirection={manager.searchParameters.sortDirection}
                        onClick={manager.changeSort}
                      />
                      <SortableHeader
                        text="Sort Order"
                        sortField="sortOrder"
                        active={manager.searchParameters.sortField === 'sortOrder'}
                        sortDirection={manager.searchParameters.sortDirection}
                        onClick={manager.changeSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {manager.systemCodePagedResults.results?.map((type: ISystemCodeDto) => (
                      <tr
                        key={type.id}
                        onClick={() => manager.selectSystemCode(type.id.toString())}
                        style={{ cursor: 'pointer' }}>
                        <td>{type.codeType}</td>
                        <td>{type.codeValue}</td>
                        <td>{type.description}</td>
                        <td>{type.active ? 'Yes' : 'No'}</td>
                        <td>{type.sortOrder}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="pt-4">
                <PagingControls
                  page={manager.systemCodePagedResults.currentPage ?? 1}
                  pageCount={manager.systemCodePagedResults.pageCount ?? 1}
                  maxPlusOrMinusPagesToShow={3}
                  onPageChange={manager.changePage}
                />
              </div>
            </>
          </Container>
        </LoadingContainer>
      </Container>
    </div>
  );
};
